import { urlFor } from "@/lib/sanity";
import { PortableTextReactComponents } from "@portabletext/react";
import React from "react";

export const usePortableTextComponents = (): Partial<PortableTextReactComponents> => {
  return {
    types: {
      image: ({ value }) => (
        <img
          src={urlFor(value)}
          alt={value.alt || "Image"}
          className="rounded-lg my-6"
        />
      ),

      horizontalRule: () => (
        <hr className="my-8 border-gray-300 dark:border-gray-700" />
      ),

      codeBlock: ({ value }) => (
        <pre className="bg-gray-800 text-white text-sm p-4 rounded-md overflow-x-auto my-6">
          <code className={`language-${value.language || "text"}`}>
            {value.code}
          </code>
        </pre>
      ),

      embed: ({ value }) => (
        <div className="my-6 aspect-video w-full max-w-full">
          <iframe
            src={value.url}
            title="Embedded media"
            className="w-full h-full rounded-lg"
            allowFullScreen
          />
        </div>
      ),

      quote: ({ value }) => (
        <blockquote className="border-l-4 border-nexus-teal pl-4 italic text-gray-700 dark:text-gray-300 my-6">
          “{value.text}”
          {value.author && (
            <footer className="mt-2 text-sm text-gray-500">— {value.author}</footer>
          )}
        </blockquote>
      ),

      callout: ({ value }) => {
        const colorMap = {
          info: "bg-blue-100 text-blue-900 border-blue-300",
          warning: "bg-yellow-100 text-yellow-900 border-yellow-300",
          success: "bg-green-100 text-green-900 border-green-300",
          error: "bg-red-100 text-red-900 border-red-300",
        };
        return (
          <div
            className={`border-l-4 p-4 my-4 rounded-md ${colorMap[value.type] || "bg-gray-100 text-gray-800 border-gray-300"}`}
          >
            {value.message}
          </div>
        );
      },

      ctaButton: ({ value }) => (
        <div className="my-6">
          <a
            href={value.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-block px-4 py-2 rounded-md font-semibold ${
              value.style === "primary"
                ? "bg-nexus-brand text-white"
                : "bg-gray-200 text-gray-800"
            }`}
          >
            {value.label}
          </a>
        </div>
      ),

      table: ({ value }) => (
        <div className="overflow-auto my-6">
          <table className="table-auto border-collapse w-full border border-gray-300 dark:border-gray-700">
            <tbody>
              {value.rows?.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.columns?.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border px-4 py-2 text-sm dark:text-gray-200"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),

      accordion: ({ value }) => {
        const [isOpen, setIsOpen] = React.useState(false);
        return (
          <div className="my-4 border border-gray-300 dark:border-gray-700 rounded-md">
            <button
              className="w-full px-4 py-2 text-left font-medium bg-gray-100 dark:bg-gray-800"
              onClick={() => setIsOpen(!isOpen)}
            >
              {value.title}
            </button>
            {isOpen && (
              <div className="px-4 py-2 text-gray-700 dark:text-gray-300">
                {value.content}
              </div>
            )}
          </div>
        );
      },

      tweet: ({ value }) => (
        <div className="my-6">
          <blockquote className="twitter-tweet">
            <a href={value.tweetUrl} target="_blank" rel="noopener noreferrer">
              View Tweet
            </a>
          </blockquote>
        </div>
      ),
    },

    marks: {
      link: ({ value, children }) => (
        <a
          href={value.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-nexus-teal underline"
        >
          {children}
        </a>
      ),
    },

    block: {
      h1: ({ children }) => <h1 className="text-4xl font-bold my-4">{children}</h1>,
      h2: ({ children }) => <h2 className="text-3xl font-semibold my-3">{children}</h2>,
      h3: ({ children }) => <h3 className="text-2xl font-semibold my-2">{children}</h3>,
      h4: ({ children }) => <h4 className="text-xl font-medium my-2">{children}</h4>,
      normal: ({ children }) => (
        <p className="my-4 text-gray-800 dark:text-gray-200">{children}</p>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 dark:text-gray-400">
          {children}
        </blockquote>
      ),
    },

    list: {
      bullet: ({ children }) => <ul className="list-disc pl-6 my-4">{children}</ul>,
      number: ({ children }) => <ol className="list-decimal pl-6 my-4">{children}</ol>,
    },

    listItem: ({ children }) => <li className="my-1">{children}</li>,
  };
};
